import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  FeedbackEmpty,
  FeedbackError,
  formatHandoverOdometer,
  InputSearch,
  PackageVehicleReasonList,
  Pagination,
  SideBarModal,
  Skeleton,
  Status,
  Tabs,
} from '@/components';
import {
  getPackageVehicles,
  getPackageVehiclesPreview,
} from '@/services/api/packages';
import {
  PackageRuleCriteria,
  PackageVehicle,
  PackageVehicles,
} from '@/types/package';
import { extractApiErrorMessage } from '@/utils';

type AvailabilityTab = 'ALL' | 'AVAILABLE' | 'UNAVAILABLE';

const TAB_FILTER: Record<AvailabilityTab, boolean | undefined> = {
  ALL: undefined,
  AVAILABLE: true,
  UNAVAILABLE: false,
};

// Tiered like the form's count, so "6 dari 6" there and here read the same:
// matches out of units ready to rent, then the whole fleet of the model.
export const describeSummary = ({
  total,
  readyToRent,
  available,
}: PackageVehicles['summary']): string =>
  readyToRent === undefined
    ? `${available} dari ${total} unit tersedia`
    : `${available} dari ${readyToRent} unit siap sewa cocok · ${total} unit di armada`;

/** A saved package's rules, or the criteria a package form is still editing. */
export type PackageVehiclesSource =
  { packageID: string } | { modelID: string; criteria: PackageRuleCriteria };

// Mounted only while open, so every open starts from the whole list.
interface PackageVehiclesModalProps {
  onClose: () => void;
  source: PackageVehiclesSource;
  packageName?: string;
}

/**
 * Every unit of the package's model and why each one is or is not stock: the
 * drill-down behind the stock count, saved or still being edited.
 */
export const PackageVehiclesModal: React.FC<PackageVehiclesModalProps> = ({
  onClose,
  source,
  packageName,
}) => {
  const [tab, setTab] = useState<AvailabilityTab>('ALL');
  // Set by the debounced onSearch, so the fetch does not follow every keystroke.
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [result, setResult] = useState<PackageVehicles | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Only the newest request may land; an older, slower one is dropped.
  const requestRef = useRef(0);
  // A string, so a parent re-render with an equal source does not refetch.
  const sourceKey = JSON.stringify(source);
  const load = useCallback(async () => {
    const request = ++requestRef.current;
    setIsFetching(true);
    setLoadError(null);
    try {
      const params = {
        page,
        size,
        search: search.trim() || undefined,
        available: TAB_FILTER[tab],
      };
      const current: PackageVehiclesSource = JSON.parse(sourceKey);
      const next =
        'packageID' in current
          ? await getPackageVehicles(current.packageID, params)
          : await getPackageVehiclesPreview({ ...current, ...params });
      if (request === requestRef.current) setResult(next);
    } catch (error) {
      if (request !== requestRef.current) return;
      setLoadError(extractApiErrorMessage(error, 'Coba muat ulang daftarnya.'));
      setResult(null);
    } finally {
      if (request === requestRef.current) setIsFetching(false);
    }
  }, [sourceKey, page, size, search, tab]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = result?.summary;
  const tabs = [
    { key: 'ALL' as const, label: 'Semua', badge: summary?.total },
    { key: 'AVAILABLE' as const, label: 'Tersedia', badge: summary?.available },
    {
      key: 'UNAVAILABLE' as const,
      label: 'Tidak tersedia',
      badge: summary?.unavailable,
    },
  ];
  const searchFor = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const items = result?.items ?? [];

  return (
    <SideBarModal
      isOpen
      onClose={onClose}
      position="center"
      width="lg"
      header={
        <div>
          <h2 className="text-[18px] font-semibold text-gray-900">
            Unit Package
          </h2>
          <p className="text-[13px] text-gray-500 mt-0.5">
            {summary ? describeSummary(summary) : (packageName ?? ' ')}
          </p>
        </div>
      }
    >
      <div className="space-y-4 px-4 py-4 md:px-6 md:py-5">
        <Tabs
          items={tabs}
          active={tab}
          onChange={(key) => {
            setTab(key);
            setPage(1);
          }}
          ariaLabel="Filter ketersediaan unit"
        />

        <InputSearch
          onSearch={searchFor}
          onClear={() => searchFor('')}
          placeholder="Cari plat nomor"
        />

        {isFetching && !result ? (
          <div className="space-y-2" aria-busy="true">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-[72px]" rounded="md" />
            ))}
          </div>
        ) : loadError ? (
          <FeedbackError
            message="Gagal memuat unit"
            description={loadError}
            onRetry={load}
          />
        ) : items.length === 0 ? (
          <FeedbackEmpty
            size="sm"
            title="Tidak ada unit"
            description={
              search
                ? `Tidak ada unit yang cocok dengan "${search}".`
                : 'Belum ada unit dengan model package ini di filter ini.'
            }
          />
        ) : (
          <ul className={`space-y-2 ${isFetching ? 'opacity-60' : ''}`}>
            {items.map((vehicle) => (
              <PackageVehicleRow key={vehicle.id} vehicle={vehicle} />
            ))}
          </ul>
        )}

        {result && result.pagination.total > 0 && (
          <Pagination
            currentPage={page}
            totalItems={result.pagination.total}
            itemsPerPage={size}
            onPageChange={setPage}
            onPageSizeChange={(next) => {
              setSize(next);
              setPage(1);
            }}
          />
        )}
      </div>
    </SideBarModal>
  );
};

const PackageVehicleRow: React.FC<{
  vehicle: PackageVehicle;
}> = ({ vehicle }) => (
  <li className="rounded-xl border border-gray-200 px-4 py-3">
    <div className="flex items-start gap-3">
      <div className="min-w-0 flex-1">
        <p className="text-[14px] font-semibold text-gray-900">
          {vehicle.platNomor}
        </p>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px] text-gray-500">
          <span className="inline-flex items-center gap-1">
            <i className="ri-map-pin-line"></i>
            {vehicle.hubName ?? 'Hub tidak diketahui'}
          </span>
          {vehicle.odometer !== null && (
            <span className="inline-flex items-center gap-1">
              <i className="ri-dashboard-3-line"></i>
              {formatHandoverOdometer(vehicle.odometer)}
            </span>
          )}
          {vehicle.ageDays !== null && (
            <span className="inline-flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              {vehicle.ageDays} hari
            </span>
          )}
        </div>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        <Status
          type="packageVehicle"
          value={vehicle.available ? 'AVAILABLE' : 'UNAVAILABLE'}
          size="small"
        />
        <Status
          type="vehicleCondition"
          value={vehicle.condition}
          size="small"
        />
      </div>
    </div>

    <PackageVehicleReasonList
      reasons={vehicle.reasons}
      className="mt-2 border-t border-gray-100 pt-2"
    />
  </li>
);
